'use client';

import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useLocale } from 'next-intl';
import { translateLegalCopy } from '@/i18n/legal-page-copy';

const TRANSLATED_ATTRIBUTES = ['aria-label', 'placeholder', 'title'] as const;

function translateTree(root: HTMLElement, locale: string) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const source = node.textContent ?? '';
    const translated = translateLegalCopy(locale, source);
    if (translated !== source) {
      const leading = source.match(/^\s*/)?.[0] ?? '';
      const trailing = source.match(/\s*$/)?.[0] ?? '';
      node.textContent = `${leading}${translated}${trailing}`;
    }
    node = walker.nextNode();
  }

  for (const element of root.querySelectorAll<HTMLElement>('*')) {
    for (const attribute of TRANSLATED_ATTRIBUTES) {
      const source = element.getAttribute(attribute);
      if (!source) continue;
      const translated = translateLegalCopy(locale, source);
      if (translated !== source) element.setAttribute(attribute, translated);
    }
  }
}

export function LegalCopyBoundary({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || locale === 'en') return;

    translateTree(root, locale);
    const observer = new MutationObserver(() => translateTree(root, locale));
    observer.observe(root, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [locale]);

  return (
    <div key={locale} ref={rootRef}>
      {children}
    </div>
  );
}
